import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * Notification preferences state
 */
interface NotificationPreferences {
  emailNotifications: boolean;
  smsNotifications: boolean;
  bookingReminders: boolean;
  marketingCommunications: boolean;
  reminderTiming: string;
}

/**
 * Toggle switch component with accessibility support
 */
function Toggle({
  id,
  label,
  description,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-slate-700 last:border-b-0">
      <div className="flex-1 pr-4">
        <label htmlFor={id} className="font-medium text-gray-900 dark:text-white cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{description}</p>
        )}
      </div>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
          checked ? 'bg-primary-600' : 'bg-gray-200 dark:bg-slate-600'
        }`}
      >
        <span className="sr-only">{label}</span>
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Notification Preferences Page
 * Allows users to manage their notification settings
 */
export function NotificationPreferencesPage() {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    emailNotifications: true,
    smsNotifications: false,
    bookingReminders: true,
    marketingCommunications: false,
    reminderTiming: '24h',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleToggle = (key: keyof NotificationPreferences) => (checked: boolean) => {
    setPreferences((prev) => ({ ...prev, [key]: checked }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleReminderTimingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPreferences((prev) => ({ ...prev, reminderTiming: e.target.value }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Simulate API call - in a real app, this would call the backend
      await new Promise((resolve) => setTimeout(resolve, 500));
      setSuccessMessage('Your notification preferences have been saved successfully.');
    } catch {
      setErrorMessage('Failed to save preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Notification Preferences - Massatherapy</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Link
              to="/settings"
              className="inline-flex items-center text-sm text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white mb-4"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Settings
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Notification Preferences
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1">
              Control how and when you receive notifications from Massatherapy.
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-green-600 dark:text-green-400 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <p className="text-green-800 dark:text-green-200">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-600 dark:text-red-400 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-red-800 dark:text-red-200">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Email Notifications Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <svg
                  className="w-5 h-5 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Email Notifications
              </h2>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              <Toggle
                id="email-notifications"
                label="Email Notifications"
                description="Receive booking confirmations and updates via email"
                checked={preferences.emailNotifications}
                onChange={handleToggle('emailNotifications')}
              />
            </div>
          </div>

          {/* SMS Notifications Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <svg
                  className="w-5 h-5 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                SMS Notifications
              </h2>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              <Toggle
                id="sms-notifications"
                label="SMS Notifications"
                description="Receive text message alerts for important updates"
                checked={preferences.smsNotifications}
                onChange={handleToggle('smsNotifications')}
              />
            </div>
          </div>

          {/* Booking Reminders Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <svg
                  className="w-5 h-5 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Booking Reminders
              </h2>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              <Toggle
                id="booking-reminders"
                label="Booking Reminders"
                description="Get reminded before your upcoming appointments"
                checked={preferences.bookingReminders}
                onChange={handleToggle('bookingReminders')}
              />

              {preferences.bookingReminders && (
                <div className="py-4">
                  <label
                    htmlFor="reminder-timing"
                    className="block font-medium text-gray-900 dark:text-white mb-1"
                  >
                    Reminder Timing
                  </label>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mb-2">
                    How long before your appointment should we remind you?
                  </p>
                  <select
                    id="reminder-timing"
                    value={preferences.reminderTiming}
                    onChange={handleReminderTimingChange}
                    className="block w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="1h">1 hour before</option>
                    <option value="2h">2 hours before</option>
                    <option value="24h">24 hours before</option>
                    <option value="48h">48 hours before</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Marketing Communications Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                <svg
                  className="w-5 h-5 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Marketing Communications
              </h2>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              <Toggle
                id="marketing-communications"
                label="Marketing Communications"
                description="Receive promotions, special offers, and wellness tips"
                checked={preferences.marketingCommunications}
                onChange={handleToggle('marketingCommunications')}
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {isSaving ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </button>
          </div>

          {/* Info Section */}
          <div className="mt-8 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="font-medium text-primary-800 dark:text-primary-200">
                  About Notifications
                </h3>
                <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                  You can change these settings at any time. Essential notifications about your
                  account security and booking changes will always be sent regardless of these
                  preferences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NotificationPreferencesPage;
